import BatchSdk from '@capsule/batch-sdk'
import consentCheck from '@capsule/consent-check'
import type { BatchData } from 'shared/batch/types/batch'
import GlobalStore from 'shared/globalStore/assets/scripts/globalStore'

declare global {
	interface Window {
		batchInit?: boolean
	}
}

/**
 * Batch
 * @module Batch
 */
export default class Batch {
	private batchConfig
	private batchConfigNotification
	private batchSdk!: BatchSdk
	private isProdDomain: boolean
	private store: GlobalStore

	/**
	 * Constructor
	 */
	constructor() {
		this.store = new GlobalStore()
		this.batchConfig = this.store.brand?.batch
		this.batchConfigNotification = this.batchConfig?.notification
		this.isProdDomain = this.store.brand?.metadata?.environment === 'prod'
	}

	/**
	 * Initialize Batch
	 */
	public init(): void {
		const batchConfig = this.getBatchConfig()
		if (batchConfig) {
			this.inject(batchConfig)
		}
		this.addServiceWorker()
	}

	private getBatchConfig(): BatchData | undefined {
		const devConfig = this.batchConfig?.dev
		const prodConfig = this.batchConfig?.prod
		const config = !devConfig || this.isProdDomain ? prodConfig : devConfig

		if (config) {
			return {
				defaultIcon: this.batchConfigNotification?.defaultIcon || '',
				smallIcon: this.batchConfigNotification?.smallIcon || '',
				ui: { native: {} },
				apiKey: config.apiKey,
				dev: config.dev,
				subdomain: config.subdomain,
				authKey: config.authKey,
				vapidPublicKey: config.vapidPublicKey
			}
		}
	}

	/**
	 * Inject method to load the Batch SDK
	 */
	private inject(config: BatchData): void {
		consentCheck('allConsentGiven', () => {
			this.batchSdk = new BatchSdk({
				config: {
					setup: config,
					showNotification: {
						title: this.batchConfigNotification?.title || '',
						options: {
							body: this.batchConfigNotification?.body || '',
							data: {
								title: ' ',
								alert: ' ',
								'com.batch': {
									da: {
										a: 'batch.deeplink',
										args: {
											l: this.batchConfigNotification?.url || '',
											newTab: true
										}
									}
								}
							}
						}
					}
				},
				onReady: () => {
					window.batchInit = true
					document.dispatchEvent(new Event('BATCH:init'))
				}
			})
			this.batchSdk.init()
		})
	}

	/**
	 * Add service worker
	 */
	private addServiceWorker(): void {
		consentCheck('allConsentGiven', () => {
			if ('serviceWorker' in navigator) {
				navigator.serviceWorker
					.register('/batchsdk-worker-loader.js')
					.then((reg) => {
						// registration worked
						console.debug(`Registration succeeded. Scope is ${reg.scope}`)
					})
					.catch((error) => {
						// registration failed
						console.error(`Registration failed with ${error}`)
					})
			}
		})
	}
}
